/**
 * Strict 4-field address sub-form, shared by the Professional Guardian
 * Create and Edit screens.
 *
 * Why a shared component:
 *   The Kathleen-Hayes regression happened because the Edit screen used to
 *   accept a single free-form string and pasted it straight into the legacy
 *   `mailing_address` DB column. Bond-creation then split that string by
 *   commas and either shifted fields or returned null for `street`. To make
 *   sure that path can never reappear, both screens render the SAME
 *   sub-form here:
 *
 *     - Google Places search box (auto-fills all 4 fields when the user
 *       picks a suggestion - matches the existing Create UX).
 *     - Street, City: free text but the comma key is blocked so users
 *       cannot recreate the comma-confusion bug locally.
 *     - State: MUI Autocomplete bound to the canonical US-state list
 *       (full names) so only valid values can be saved.
 *     - ZIP: digits-only mask (5 or ZIP+4).
 *     - Per-field blur validation via `validateAddressFields` from
 *       globals/utils/addressValidation.
 *
 * The parent owns the state object `{ street_address, city, state, zipCode }`
 * and the consolidated `errors` map keyed by `${fieldPrefix}<field>`. The
 * parent passes `setErrors(updaterFn)` so the sub-form can both set and
 * clear individual errors using a single React-style updater pattern.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  TextField,
  Box,
  Typography,
  InputAdornment,
  Alert,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SearchIcon from "@mui/icons-material/Search";
import InputMask from "react-input-mask";
import { loadGoogleMaps } from "../utils/googleMapsLoader";
import {
  validateAddressFields,
  STATE_OPTIONS,
  normalizeZipCodeForForm,
} from "../utils/addressValidation";

// ---------------------------------------------------------------------------
// Google Places search input (was previously inlined in
// ProfessionalGuardianCreate.jsx). Lifted here so Edit benefits from it too.
// ---------------------------------------------------------------------------
function GoogleAddressSearch({ placeholder, onSelect, disabled }) {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps()
      .then(() => {
        if (mounted) setLoaded(true);
      })
      .catch((e) => console.error("Google Maps loader error:", e));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!loaded || !window.google || !inputRef.current) return;

    autoCompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      { fields: ["address_components"], types: ["geocode"] }
    );

    const listener = autoCompleteRef.current.addListener(
      "place_changed",
      () => {
        const place = autoCompleteRef.current.getPlace();
        if (!place || !place.address_components) {
          console.error(
            "No valid place returned or missing address components."
          );
          return;
        }

        let streetNumber = "";
        let route = "";
        let city = "";
        let state = "";
        let zipCode = "";

        place.address_components.forEach((component) => {
          const type = component.types[0];
          switch (type) {
            case "street_number":
              streetNumber = component.long_name;
              break;
            case "route":
              route = component.long_name;
              break;
            case "locality":
              city = component.long_name;
              break;
            case "sublocality":
            case "sublocality_level_1":
              if (!city) city = component.long_name;
              break;
            case "administrative_area_level_1":
              state = component.long_name;
              break;
            case "postal_code":
              zipCode = component.long_name;
              break;
            default:
              break;
          }
        });

        const street = `${streetNumber} ${route}`.trim();
        if (onSelect) {
          onSelect({
            street_address: street,
            city,
            state,
            zipCode: normalizeZipCodeForForm(zipCode),
          });
        }

        // Clear the visible search input after selection so the placeholder
        // is shown again - the data lives in the 4 fields below.
        if (inputRef.current) inputRef.current.value = "";
      }
    );

    return () => {
      if (listener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [loaded, onSelect]);

  return (
    <TextField
      variant="outlined"
      fullWidth
      placeholder={placeholder}
      inputRef={inputRef}
      disabled={disabled}
      sx={{ marginBottom: "8px" }}
      slotProps={{ input: {
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}}
    />
  );
}

// Block the comma key in Street/City. The legacy DB column joins the 4 parts
// with ", " - allowing a comma inside any one part would corrupt downstream
// parsing the same way the Kathleen-Hayes row did.
const blockComma = (e) => {
  if (e.key === ",") {
    e.preventDefault();
  }
};

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
/**
 * @param {object} props
 * @param {string} props.label                 - Visible section label (e.g. "Mailing Address").
 * @param {{street_address:string, city:string, state:string, zipCode:string}} props.value
 * @param {(next:object)=>void} props.onChange - Called with the FULL next address object.
 * @param {Object<string,string>} [props.errors]
 * @param {(updater:Function)=>void} [props.setErrors] - React-style errors updater.
 * @param {string} [props.fieldPrefix]         - e.g. "mailing_address.".
 * @param {string} [props.rawValueIfUnparsed]  - Legacy single-string value (only used
 *                                               to render the warning Alert).
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.hideSearch]
 */
export default function AddressSubForm({
  label,
  value,
  onChange,
  errors = {},
  setErrors,
  fieldPrefix = "",
  rawValueIfUnparsed,
  disabled = false,
  hideSearch = false,
}) {
  const safeValue = value || {
    street_address: "",
    city: "",
    state: "",
    zipCode: "",
  };

  const errKey = useCallback((name) => `${fieldPrefix}${name}`, [fieldPrefix]);

  // Update one field, clear that field's error (the user is editing it).
  const setField = useCallback(
    (field, fieldValue) => {
      onChange({ ...safeValue, [field]: fieldValue });
      if (setErrors) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[errKey(field)];
          return next;
        });
      }
    },
    [onChange, safeValue, setErrors, errKey]
  );

  const handleSelectFromGoogle = useCallback(
    (next) => {
      // Google supplies all 4 fields at once; replace wholesale and clear
      // any prior errors on this sub-form.
      const merged = { ...safeValue, ...next };
      if (next && Object.prototype.hasOwnProperty.call(next, "zipCode")) {
        merged.zipCode = normalizeZipCodeForForm(next.zipCode);
      }
      onChange(merged);
      if (setErrors) {
        setErrors((prev) => {
          const out = { ...prev };
          ["street_address", "city", "state", "zipCode"].forEach((k) => {
            delete out[errKey(k)];
          });
          return out;
        });
      }
    },
    [onChange, safeValue, setErrors, errKey]
  );

  // Per-field blur validation. We re-run validateAddressFields against the
  // CURRENT (post-blur) value but only commit the error for the field that
  // just blurred - so other fields don't light up red prematurely.
  const handleBlur = useCallback(
    (field) => () => {
      if (!setErrors) return;
      const allErrors = validateAddressFields(safeValue, {
        requireAll: true,
        fieldPrefix,
      });
      const key = errKey(field);
      const msg = allErrors[key];
      setErrors((prev) => {
        const next = { ...prev };
        if (msg) {
          next[key] = msg;
        } else {
          delete next[key];
        }
        return next;
      });
    },
    [safeValue, fieldPrefix, errKey, setErrors]
  );

  // Detect "raw value present but couldn't be split" so the Alert is shown
  // only when truly needed. Once the user fills all 4 fields it disappears.
  const hasRawWarning =
    rawValueIfUnparsed &&
    (!safeValue.street_address ||
      !safeValue.city ||
      !safeValue.state ||
      !safeValue.zipCode);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
        {label}
      </Typography>

      {hasRawWarning && (
        <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
          The address on file (<em>{rawValueIfUnparsed}</em>) could not be split
          into all 4 components. Use the search box or fill the fields below
          before saving.
        </Alert>
      )}

      {!hideSearch && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <GoogleAddressSearch
            placeholder={`Search ${label}`}
            onSelect={handleSelectFromGoogle}
            disabled={disabled}
          />
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            required
            label="Street Address"
            value={safeValue.street_address || ""}
            onChange={(e) => setField("street_address", e.target.value)}
            onBlur={handleBlur("street_address")}
            onKeyDown={blockComma}
            error={!!errors[errKey("street_address")]}
            helperText={errors[errKey("street_address")] || " "}
            disabled={disabled}
            slotProps={{ htmlInput: { maxLength: 200 }}}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            required
            label="City"
            value={safeValue.city || ""}
            onChange={(e) => setField("city", e.target.value)}
            onBlur={handleBlur("city")}
            onKeyDown={blockComma}
            error={!!errors[errKey("city")]}
            helperText={errors[errKey("city")] || " "}
            disabled={disabled}
            slotProps={{ htmlInput: { maxLength: 100 }}}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Autocomplete
            options={STATE_OPTIONS}
            getOptionLabel={(opt) =>
              typeof opt === "string" ? opt : opt?.label || ""
            }
            isOptionEqualToValue={(opt, val) => {
              const a = typeof opt === "string" ? opt : opt?.label;
              const b = typeof val === "string" ? val : val?.label;
              return (a || "").toLowerCase() === (b || "").toLowerCase();
            }}
            value={safeValue.state || null}
            onChange={(_, picked) => {
              const next =
                typeof picked === "string" ? picked : picked?.label || "";
              setField("state", next);
            }}
            onBlur={handleBlur("state")}
            disabled={disabled}
            freeSolo={false}
            autoHighlight
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="State"
                error={!!errors[errKey("state")]}
                helperText={
                  errors[errKey("state")] || "Pick a US state (full name)"
                }
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <InputMask
            mask="99999-9999"
            maskChar=""
            value={safeValue.zipCode || ""}
            onChange={(e) =>
              setField("zipCode", normalizeZipCodeForForm(e.target.value))
            }
            onBlur={handleBlur("zipCode")}
            disabled={disabled}
          >
            {(inputProps) => (
              <TextField
                {...inputProps}
                fullWidth
                required
                label="ZIP/Postal Code"
                error={!!errors[errKey("zipCode")]}
                helperText={
                  errors[errKey("zipCode")] ||
                  "5 digits (e.g. 33773) or ZIP+4 (e.g. 33773-1234)"
                }
                slotProps={{ htmlInput: {
                  ...(inputProps.inputProps || {}),
                  inputMode: "numeric",
                }}}
              />
            )}
          </InputMask>
        </Grid>
      </Grid>
    </Box>
  );
}

export { GoogleAddressSearch };
